import { useState, useRef, useCallback, useEffect } from 'react';
import { clearBlogFiltersCache } from './utils/cacheUtils';

interface UseUnifiedFiltersProps {
  initialCategory?: string | number | null;
  initialSearchTerm?: string;
}

export const useUnifiedFilters = ({
  initialCategory = null,
  initialSearchTerm = ''
}: UseUnifiedFiltersProps = {}) => {
  // Funzione per convertire la categoria in un formato coerente
  const parseCategory = (cat: string | number | null | undefined): number | null => {
    if (cat === undefined || cat === null || cat === '') return null;
    
    const numericCategory = typeof cat === 'string' ? parseInt(cat, 10) : cat;
    return !isNaN(numericCategory as number) ? numericCategory : null;
  };
  
  // Stati unificati per categoria e ricerca
  const [categoryId, setCategoryId] = useState<number | null>(parseCategory(initialCategory));
  const [categoryName, setCategoryName] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>(initialSearchTerm || '');
  const [searchInputValue, setSearchInputValue] = useState<string>(initialSearchTerm || '');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Riferimenti per tracciare i cambiamenti
  const filtersChanged = useRef<boolean>(false);
  const currentRequestId = useRef<number>(0);
  
  // Gestione del debounce per la ricerca
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Funzione per gestire il click su una categoria
  const handleCategoryClick = useCallback((newCategoryId: number, newCategoryName: string) => {
    // Incrementa l'ID della richiesta per annullare richieste obsolete
    currentRequestId.current++;
    const requestId = currentRequestId.current;
    
    // Imposta lo stato di caricamento
    setIsLoading(true);
    
    // Gestisce il toggle della stessa categoria (deseleziona) o seleziona una nuova
    if (newCategoryId === categoryId) {
      console.log(`[useUnifiedFilters] Deselecting category ${newCategoryId}`);
      setCategoryId(null);
      setCategoryName('');
    } else {
      console.log(`[useUnifiedFilters] Selecting category ${newCategoryId} (${newCategoryName})`);
      setCategoryId(newCategoryId);
      setCategoryName(newCategoryName);
    }
    
    // Resetta la ricerca quando si cambia categoria
    if (searchInputValue) {
      setSearchInputValue('');
      setSearchTerm('');
    }
    
    // Marca i filtri come cambiati
    filtersChanged.current = true;
    
    // Invalida la cache una sola volta
    clearBlogFiltersCache();
    
    // Resetta lo stato di caricamento dopo un breve ritardo
    // ma solo se questa è ancora la richiesta più recente
    setTimeout(() => {
      if (requestId === currentRequestId.current) {
        setIsLoading(false);
      }
    }, 500);
  }, [categoryId, searchInputValue]);
  
  // Funzione per gestire il cambio del termine di ricerca
  const handleSearchChange = useCallback((value: string) => {
    // Aggiorna immediatamente il valore dell'input
    setSearchInputValue(value);
    
    // Cancella eventuali timeout precedenti
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    // Incrementa l'ID della richiesta
    currentRequestId.current++;
    const requestId = currentRequestId.current;
    
    // Imposta lo stato di caricamento
    setIsLoading(true);
    
    // Applica il termine di ricerca dopo un breve ritardo (debounce)
    searchTimeout.current = setTimeout(() => {
      // Applica il termine di ricerca solo se questa è ancora la richiesta più recente
      if (requestId === currentRequestId.current) {
        console.log(`[useUnifiedFilters] Setting search term: "${value}"`);
        setSearchTerm(value);
        filtersChanged.current = true;
        
        // Resetta lo stato di caricamento
        setTimeout(() => {
          if (requestId === currentRequestId.current) {
            setIsLoading(false);
          }
        }, 300);
      }
    }, 300);
  }, []);
  
  // Funzione per resettare tutti i filtri
  const handleResetFilters = useCallback(() => {
    console.log('[useUnifiedFilters] Resetting all filters');
    
    // Incrementa l'ID della richiesta
    currentRequestId.current++;
    
    // Cancella eventuali timeout di ricerca
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
      searchTimeout.current = null;
    }
    
    // Resetta tutti gli stati
    setCategoryId(null);
    setCategoryName('');
    setSearchTerm('');
    setSearchInputValue('');
    setIsLoading(true);
    
    // Marca i filtri come cambiati
    filtersChanged.current = true;
    
    // Invalida la cache
    clearBlogFiltersCache();
    
    // Resetta lo stato di caricamento dopo un breve ritardo
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  }, []);
  
  // Pulisce i timeout quando il componente viene smontato
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);
  
  return {
    // Stati
    categoryId,
    categoryName,
    searchTerm,
    searchInputValue,
    isLoading,
    
    // Azioni
    handleCategoryClick,
    handleSearchChange,
    handleResetFilters,
    
    // Riferimenti
    filtersChanged,
    currentRequestId
  };
};
